import {
  IS_MOUSE_USER_CSS,
  IS_TOUCH_USER_CSS,
  UserPointerType
} from "./types";
import LocalStorageService from "external-services/local-storage-service/localStorage.service";
import {LocalStorageKey} from "shared-types/SharedEnums";
import DateUtilsService from "external-services/date-utils-service";
import LocationService from "external-services/location-service";

const NS = 'PointerDetectionService';

class PointerDetectionService {

  private resolverCB: (value: UserPointerType) => void;

  detectPointerType(cb: (value: UserPointerType) => void) {
    this.resolverCB = cb;

    this.addMouseHandler();
    this.addTouchHandler();
  }

  isIOSTouch = (state: {settingReducer: {currentPointerDevice: UserPointerType}}) => {
    const isIOS = LocationService.isIOS();
    const {settingReducer: {currentPointerDevice}} = state;

    // it is possible for iPads to also use a mouse, so we must check specifically for touch usage
    return isIOS && currentPointerDevice === UserPointerType.TouchUser;
  }

  private setPointerType(pointerType: UserPointerType) {
    this.resolverCB(pointerType);

    // sets CSS classes on the body element for convenient reference
    if (pointerType === UserPointerType.MouseUser) {
      document.body.classList.remove(IS_TOUCH_USER_CSS);
      document.body.classList.add(IS_MOUSE_USER_CSS);

      // if mouse user is detected, we add a listener for touch usage, just in case the user changes pointer type
      this.addTouchHandler(false);

    } else if (pointerType === UserPointerType.TouchUser) {
      document.body.classList.remove(IS_MOUSE_USER_CSS);
      document.body.classList.add(IS_TOUCH_USER_CSS);

      // if touch user is detected, we add a listener for mouse usage, just in case the user changes pointer type
      this.addMouseHandler(false);
    }
  }

  private addMouseHandler(includeLocalStorageCheck = true) {

    if (includeLocalStorageCheck) {

      const pointerTypeData = LocalStorageService.get(LocalStorageKey.pointerType);
      const data = pointerTypeData ? JSON.parse(pointerTypeData) : null;

      if (data?.pointerType === UserPointerType.MouseUser) {
        const isTimeExpired = DateUtilsService.isAfterNow(data.expirationDate)
        if (!isTimeExpired) {
          this.setPointerType(UserPointerType.MouseUser);
          return;
        }
      }
    }

    /**
     * Strangely, iPads and other touch devices still trigger the "mousemove" event occasionally, even when there is no
     * mouse connected. Using pointermove and then evt.pointerType === "mouse" worked reliably though.
     */
    const handleMouseMove = (evt: PointerEvent) => {

      if (evt.pointerType === "mouse") {
        this.setPointerType(UserPointerType.MouseUser);
        document.body.removeEventListener('pointermove', handleMouseMove);
      }

      this.setLocalStorage(UserPointerType.MouseUser);
    }

    document.body.addEventListener('pointermove', handleMouseMove);
  }

  private addTouchHandler(includeLocalStorageCheck = true) {

    if (includeLocalStorageCheck) {

      const pointerTypeData = LocalStorageService.get(LocalStorageKey.pointerType);
      const data = pointerTypeData ? JSON.parse(pointerTypeData) : null;

      if (data?.pointerType === UserPointerType.TouchUser) {
        const isTimeExpired = DateUtilsService.isAfterNow(data.expirationDate)
        if (!isTimeExpired) {
          this.setPointerType(UserPointerType.TouchUser);
          return;
        }
      }
    }

    const handleTouchStart = () => {
      this.setPointerType(UserPointerType.TouchUser);
      document.body.removeEventListener('touchstart', handleTouchStart);

      this.setLocalStorage(UserPointerType.TouchUser);
    }

    document.body.addEventListener('touchstart', handleTouchStart);
  }

  private setLocalStorage(pointerType: UserPointerType) {
    const expirationDate = DateUtilsService.getDateInFuture(1, 'days', true) as string;
    const data = {pointerType, expirationDate};
    LocalStorageService.set(LocalStorageKey.pointerType, JSON.stringify(data));
  }
}

const instance = new PointerDetectionService();
export default instance as PointerDetectionService;
